import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import React, { useContext } from 'react';
import { NintendoAccountFamilyUser, PartyMember } from '../../../../../utils/types';
import { localizedStrings } from '../../../../../context/LocalizationContext';
import AccountContext from '../../../../../context/AccountContext';

interface Props {
    selectedGuests: NintendoAccountFamilyUser[] | PartyMember[];
    isDrawing: boolean;
    isActivity?: boolean;
    isEventGate?: boolean;
    onClose: () => void;
    onConfirm: () => void;
    maxAttendees?: number;
}

/**
 * Modal for confirming entry or ticket with selected guests.
 */
const ConfirmModal = ({
    selectedGuests,
    isDrawing,
    isActivity,
    isEventGate,
    onClose,
    onConfirm,
    maxAttendees = 1,
}: Props) => {
    const { value: accountInfo } = useContext(AccountContext);
    const accountNickName = accountInfo.nickname;

    // Host's nickname is added for non-event gate reservations since the guest list not not include the host.
    let nickNames = isEventGate
        ? selectedGuests.map((guest) => guest.nickname)
        : [accountNickName, ...selectedGuests.map((guest) => guest.nickname)];

    if (nickNames.length === 0) {
        if (maxAttendees > 1) {
            nickNames = [accountNickName];
        }
    }

    const totalGuests = nickNames.length;

    let modalTitle = localizedStrings.tickets.registration.guest.confirmTicketsTitle;
    if (isDrawing) {
        modalTitle = localizedStrings.tickets.registration.guest.confirmEntryTitle;
    } else if (isActivity) {
        modalTitle = localizedStrings.tickets.registration.guest.confirmPassesTitle;
    }

    let modalBody = localizedStrings.formatString(
        localizedStrings.tickets.registration.guest.confirmTicketsMessage,
        { totalGuests },
    );

    if (isActivity && isDrawing) {
        modalBody = localizedStrings.formatString(
            localizedStrings.tickets.registration.guest.confirmPassesEntryMessage,
            { totalGuests },
        );
    } else if (isActivity) {
        modalBody = localizedStrings.formatString(
            localizedStrings.tickets.registration.guest.confirmPassesMessage,
            { totalGuests },
        );
    } else if (isDrawing) {
        modalBody = localizedStrings.formatString(
            localizedStrings.tickets.registration.guest.confirmEntryMessage,
            { totalGuests },
        );
    }

    return (
        <Modal show onHide={onClose} data-testid="confirm-modal">
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{modalBody}</p>
                <div>
                    {nickNames.map((nickname) => (
                        <ListGroup.Item data-testid="guest-nickname-item">
                            {nickname}
                        </ListGroup.Item>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-secondary"
                    data-testid="confirm-modal-make-changes-btn"
                    className="me-auto"
                    onClick={onClose}
                >
                    {localizedStrings.tickets.waitlist.makeChanges}
                </Button>
                <Button
                    variant="primary"
                    data-testid="confirm-modal-confirm-btn"
                    onClick={onConfirm}
                >
                    {localizedStrings.general.confirm}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmModal;
